import * as CL from '@design-system/component-library';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { dsClass } from '../../common/constants/dsClasses.js';
import { CommonErrorType } from '../../common/enums.js';
import { fieldCantBeEmptyMsg, submitOrderMsg, t } from '../../common/i18n/index.js';
import {
  CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE,
  filterAndGetDefaultBillingAccountId,
  getEmptyBillingAccount,
  getReceiverType,
  prepareBillingAccountSave,
} from '../../common/utils/billingAccountUtils.js';
import { validCampaignsPresent } from '../../common/utils/campaignUtils.js';
import { deepEqual } from '../../common/utils/objectUtils.js';
import { SalesType } from '../../generated/api/salesType';
import { AddOrSelectBillingAccounts } from '../AddOrSelectBillingAccountsV2/AddOrSelectBillingAccounts.js';
import { Voucher } from '../OrderSubscriptionSelection/Voucher.js';
import { OrderSummary, OrderSummaryType } from '../OrderSummary/OrderSummary.js';
import type { ConfiguredOffer } from '../../common/types/commercialProduct.js';
import type { BillingAccountOrErrorSupplier, CommonError } from '../../common/types/errors.js';
import type {
  BillChannel,
  BillingAccount,
  BillingAccountsResponse,
  CampaignAssociation,
  CompanyInfoResponse,
  Contact,
  OnlineModel,
} from '../../generated/api/models.js';
import type { State } from '../../selfservice/common/store.js';
import type { Dispatch, SetStateAction } from 'react';

export interface FixedBroadbandBillingInformationProps {
  onSubmit: (existingBaDispIdOrNewBa: string | BillingAccount | undefined) => void;
  fixedBroadBandOnlineModel: OnlineModel;
  configuredOffer: ConfiguredOffer;
  selectedCampaign?: CampaignAssociation;
  setCampaignAutoSelect: Dispatch<SetStateAction<boolean>>;
  billChannels: BillChannel[];
  billingAccounts: BillingAccountsResponse;
  contacts: Contact[];
  companyInfo?: CompanyInfoResponse;
}

const getBillingAccountErrors = (billingAccountsErrors: CommonError[] = [], noBillingAccountError?: CommonError) =>
  noBillingAccountError ? [...billingAccountsErrors, noBillingAccountError] : billingAccountsErrors;

export const FixedBroadbandBillingInformation = ({
  billChannels,
  billingAccounts,
  onSubmit,
  configuredOffer,
  fixedBroadBandOnlineModel,
  selectedCampaign,
  setCampaignAutoSelect,
  companyInfo,
  contacts,
}: FixedBroadbandBillingInformationProps) => {
  const [selectedBaId, setSelectedBaId] = useState<string | undefined>();
  const [handleBAValues, setHandleBAValues] = useState<BillingAccountOrErrorSupplier>();
  const [noBillingAccountError, setNoBillingAccountError] = useState<CommonError>();

  useEffect(() => {
    if (!selectedBaId && billingAccounts?.searchResults) {
      setSelectedBaId(
        billingAccounts?.searchResults?.length
          ? filterAndGetDefaultBillingAccountId(billingAccounts)
          : CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE
      );
    }
  }, [selectedBaId, billingAccounts]);

  const { campaigns } = useSelector(
    (state: State) => ({
      campaigns: state.campaigns,
    }),
    deepEqual
  );
  const billingAccountsErrors = useMemo(
    () => getBillingAccountErrors(undefined, noBillingAccountError),
    [noBillingAccountError]
  );
  const campaignErrors = campaigns?.errors || [];
  const campaignContextsFromVoucher = campaigns?.campaignContexts || [];
  const activeCampaignWithFixedPeriod =
    campaignErrors.length === 0 &&
    validCampaignsPresent(campaignContextsFromVoucher) &&
    selectedCampaign?.fixedTermPeriod;

  const handleSubmit = async () => {
    if (selectedBaId === undefined) {
      setNoBillingAccountError({
        type: CommonErrorType.VALIDATION,
        field: 'selectBillingAccount',
        message: t.VPVR(fieldCantBeEmptyMsg),
      });
    } else if (handleBAValues && selectedBaId === CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE) {
      const { obj } = await handleBAValues();
      const values = obj as BillingAccount;
      const receiverType = getReceiverType(obj);
      const updatedBillingAccount = prepareBillingAccountSave(getEmptyBillingAccount('company'), values, receiverType);
      onSubmit(updatedBillingAccount);
    } else {
      const billingAccount = billingAccounts?.searchResults?.find(ba => ba.result.billingAccountId === selectedBaId);
      onSubmit(billingAccount?.result?.billingAccountDisplayId);
    }
  };

  return (
    <div className="of-selfservice">
      {billingAccounts?.searchResults && (
        <AddOrSelectBillingAccounts
          addNewBA={true}
          billingAccounts={billingAccounts.searchResults}
          billChannels={billChannels}
          selectedBaId={selectedBaId}
          getBillingAccountSaveValuesFn={selectedBaData => {
            const { selectedBaId: selBaId, prepareNewBillingAccountSaveValues } = selectedBaData();
            setHandleBAValues(() => prepareNewBillingAccountSaveValues);
            if (selBaId && selBaId !== CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE) {
              setNoBillingAccountError(undefined);
            }
            if (selBaId) {
              setSelectedBaId(selBaId);
            }
          }}
          createBillingAccountErrors={billingAccountsErrors}
          companyInfo={companyInfo}
          contacts={contacts}
        />
      )}
      <Voucher
        onlineModelCode={fixedBroadBandOnlineModel.onlineModelCode}
        salesType={SalesType.NEW_SALE}
        onClear={() => setCampaignAutoSelect(true)}
        onSubmit={() => setCampaignAutoSelect(true)}
      />
      {activeCampaignWithFixedPeriod && campaigns?.voucher && (
        <div className={dsClass.MARGIN_BOTTOM_4}>
          {t.H0N7(
            'Please note that the voucher code "{}" will make the subscription fixed-term and thus it can not be terminated in the middle of the contract period.',
            campaigns.voucher
          )}
        </div>
      )}
      <OrderSummary
        commercialProducts={configuredOffer.selectedCommercialProducts}
        summaryType={OrderSummaryType.DETAILS_CLOSED}
      />
      <CL.Button size="l" className={`${dsClass.MARGIN_TOP_4} of-next-button`} onClick={handleSubmit}>
        {t.RZU4(submitOrderMsg)}
      </CL.Button>
    </div>
  );
};
